import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseSiteService } from '@spartacus/core';
import { Order } from '@spartacus/order/root';
import * as moment from 'moment';
import { Observable, Subscription, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, pluck, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';
import { CheckoutConstants } from 'src/app/model/checkout.model';
import { DealerPrice } from 'src/app/model/dealer-price.model';
import { BaseStoreService } from 'src/app/service/base-store/base-store.service';
import { ShimanoBusinessTypeService } from 'src/app/service/businessType/shimano-business-type.service';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { CustomTranslationService } from 'src/app/service/language/custom-translation.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { OrderHistoryService } from 'src/app/service/orders/order-history.service';
import { FREIGHT, SnahCountryService } from 'src/app/service/snah/snah-countries.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { getOrderHistoryStatusIcon } from 'src/app/spartacus/features/order/order-history/order-history.component';
import { OrderDetailsService } from '../../../../../service/myaccount/order-details.service';

export interface ShimanoOrder extends Order {
  purchaseOrderNumber?: string;
  createdDateString?: Date;
  placedBy?: string;
  deliverFullOrder?: boolean;
  erpOrderNumber?: string;
  statusCode?: string;
  statusDisplay?: string;
  orderType?: string;
  orderTypeCode?: string;
}
@Component({
  selector: 'app-order-detail-summary',
  templateUrl: './order-detail-summary.component.html',
  styleUrls: ['./order-detail-summary.component.scss'],
  standalone: false,
})
export class OrderDetailSummaryComponent implements OnInit, OnDestroy {
  orderId: string;
  erpOrderType: string;
  order$: Observable<ShimanoOrder>;
  order: any;
  showSrp = false;
  srpSubscription$: Subscription;
  reloadOrderDetailsSubscription$: Subscription;
  dealerPrices: DealerPrice[];
  default = 'default';
  showDeliveryFee: boolean;
  showOrderType: boolean;
  features: any = {};
  baseSite$: Observable<string> = this.baseService.getActive();
  OrderDetailsHideDiscountEnabled: boolean;
  orderSubscription$: Subscription;
  isCustomTranslationsEnabledSubs$: Subscription;
  isTaxRelatedCustomTranslationsEnabledSubs$: Subscription;
  loadCheckoutConstantsSubscription$: Subscription;
  isCustomTranslationsEnabled: boolean;
  isTaxRelatedCustomTranslationsEnabled: boolean;
  showDynamicDateFormat: boolean;
  showSplitDelivery: boolean;
  showOrderTypes: boolean;
  showFreeDelivery: boolean;
  lastActiveCountry: string;

  activeCountry$: Observable<string> = this.countryService.getActive();
  activeLanguage$: Observable<string> = this.languageService.getActive();

  isCustomTranslationsEnabled$(customKeys: string[]): Observable<boolean> {
    return combineLatest([this.activeCountry$, this.activeLanguage$]).pipe(
      switchMap(([activeCountry, activeLanguage]) =>
        this.customTranslationService.isCustomTranslationsEnabled$(activeCountry, activeLanguage, customKeys)
      )
    );
  }
  dateFormat$: Observable<string> = this.baseStoreService.getBasestoreData().pipe(pluck('dateFormat'));
  showActiveSnahCountry$: Observable<boolean> = this.SnahCountryService.getSnahFeatures(FREIGHT);

  constructor(
    protected orderDetailsService: OrderDetailsService,
    private route: ActivatedRoute,
    private componentService: ComponentService,
    private cdr: ChangeDetectorRef,
    private baseService: BaseSiteService,
    private shimanoBusinessTypeService: ShimanoBusinessTypeService,
    private shimanoCountryService: ShimanoCountryService,
    private languageService: ShimanoLanguageService,
    private countryService: ShimanoCountryService,
    private customTranslationService: CustomTranslationService,
    private checkoutServiceExtends: CheckoutServiceExtends,
    private baseStoreService: BaseStoreService,
    private storageService: StorageService,
    private SnahCountryService: SnahCountryService,
    private orderHistoryService: OrderHistoryService
  ) {
    this.route.params.pipe(take(1)).subscribe((params) => {
      this.orderId = params.orderCode;
    });

    this.route.queryParams.pipe(take(1)).subscribe((data) => {
      this.erpOrderType = data?.erpOrderType;
    });

    this.orderDetailsService
      .getOrderHistoryConfig()
      .pipe(take(1))
      .subscribe((resp: any) => {
        this.showDeliveryFee = resp[this.baseSiteId]
          ? resp[this.baseSiteId].showDeliveryFee
          : resp[this.default].showDeliveryFee;

        this.showOrderType = resp.showOrderType[this.businessType][this.country]
          ? resp.showOrderType[this.businessType][this.country]
          : resp[this.default].showOrderType;

        this.cdr.detectChanges();
      });

    // get customized order history features
    this.orderHistoryService
      .getFeatures()
      .pipe(take(1))
      .subscribe((features: any) => {
        this.features = { ...features, ...this.features };
        this.cdr.detectChanges();
      });
  }
  get baseSiteId(): string {
    let baseSiteId: string;
    this.baseSite$.pipe(take(1)).subscribe((baseSiteData) => (baseSiteId = baseSiteData));
    return baseSiteId;
  }

  get businessType(): string {
    let businessType: string;
    this.shimanoBusinessTypeService
      .getActive()
      .pipe(take(1))
      .subscribe((data) => (businessType = data));
    return businessType;
  }

  get country(): string {
    let country: string;
    this.shimanoCountryService
      .getActive()
      .pipe(take(1))
      .subscribe((data) => (country = data));
    return country;
  }

  ngOnInit() {
    this.orderSubscription$ = this.languageService
      .getActive()
      .pipe(
        distinctUntilChanged(),
        switchMap(() => of(this.componentService.orderDetailsEntries$.getValue())),
        tap((orderDetails) => {
          this.order = orderDetails;
          this.componentService.setOrderDetails(orderDetails);
          this.addFeatures(orderDetails);
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    this.loadCheckoutConstants();
    this.setIsCustomTranslationsEnabled();

    this.reloadOrderDetailsSubscription$ = this.componentService.reloadOrderDetails$
      .pipe(
        switchMap(() => this.orderDetailsService.getOrderDetails(this.orderId, this.erpOrderType)),
        tap((orderDetails) => {
          this.order = orderDetails;
          this.componentService.setOrderDetails(orderDetails);
          this.componentService.orderDetailsEntries$.next(orderDetails); // Fallback
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    this.getInitSrpToggleValue();

    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrp = checked;
      this.cdr.detectChanges();
    });
    this.isOrderDetailsHideDiscount();
  }

  setIsCustomTranslationsEnabled(): void {
    this.isCustomTranslationsEnabledSubs$ = this.isCustomTranslationsEnabled$([
      'myAccount.orderDetails.orderSummary.orderNumber',
    ]).subscribe((data) => {
      this.isCustomTranslationsEnabled = data;
      this.cdr.detectChanges();
    });

    this.isTaxRelatedCustomTranslationsEnabledSubs$ = this.isCustomTranslationsEnabled$([
      'myAccount.orderDetails.orderTotalSummary.subtotal',
      'myAccount.orderDetails.orderTotalSummary.tax',
      'myAccount.orderDetails.orderTotalSummary.total',
    ]).subscribe((data) => {
      this.isTaxRelatedCustomTranslationsEnabled = data;
      this.cdr.detectChanges();
    });
  }

  loadCheckoutConstants() {
    this.loadCheckoutConstantsSubscription$ = this.activeCountry$
      .pipe(
        shareReplay(1),
        tap((activeCountry) => {
          this.lastActiveCountry = activeCountry;
        }),
        startWith(this.lastActiveCountry),
        switchMap((activeCountry) =>
          combineLatest([
            this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.DYNAMIC_DATE_FORMAT),
            this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.SPLIT_DELIVERY),
            this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.ORDER_TYPE),
            this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.FREE_DELIVERY),
          ])
        )
      )
      .subscribe(([dynamicDateFormat, splitDelivery, orderTypes, freeDelivery]) => {
        this.showDynamicDateFormat = dynamicDateFormat;
        this.showSplitDelivery = splitDelivery;
        this.showOrderTypes = orderTypes;
        this.showFreeDelivery = freeDelivery;
        this.cdr.detectChanges();
      });
  }

  getInitSrpToggleValue() {
    this.showSrp = this.storageService.getLocalStorageItem('showSrp') === 'true';
  }

  getStatusIcon(statusCode: string): string {
    return getOrderHistoryStatusIcon(statusCode);
  }

  setCustomContext(isCustomTranslationsEnabled: boolean, activeCountry: string, activeLanguage: string) {
    return isCustomTranslationsEnabled ? [activeCountry, activeLanguage].join('-') : null;
  }

  hasNoFreeDelivery(order: any): string {
    if (order?.deliveryCost === undefined || (order?.deliveryCost?.value === 0 && !order?.freeShipping)) {
      return 'feeInProgress';
    } else if (order?.deliveryCost && order?.deliveryCost?.value > 0 && !order?.freeShipping) {
      return 'shippingFee';
    }
  }

  isOrderDetailsHideDiscount(): void {
    this.orderDetailsService
      .getOrderDetailsHideDiscount$()
      .pipe(take(1))
      .subscribe((enabled) => {
        this.OrderDetailsHideDiscountEnabled = enabled;
        this.cdr.detectChanges();
      });
  }

  getTimezoneFromDate(dateString: string): string {
    const date = moment.parseZone(dateString);
    return date.format('Z');
  }

  addFeatures(order: ShimanoOrder): void {
    const orderType = order.orderTypeCode;
    this.features.hideOrderStatus = ['CO', 'SW'].includes(orderType);
    this.features.hideOrderBySplitDeliveryField = ['CO', 'SW'].includes(orderType);
  }

  ngOnDestroy() {
    this.srpSubscription$?.unsubscribe();
    this.reloadOrderDetailsSubscription$?.unsubscribe();
    this.orderSubscription$?.unsubscribe();
    this.isCustomTranslationsEnabledSubs$?.unsubscribe();
    this.isTaxRelatedCustomTranslationsEnabledSubs$?.unsubscribe();
    this.loadCheckoutConstantsSubscription$?.unsubscribe();
  }
}
